import React from 'react';
import Dialog from 'components/pure/utils/Dialog';
import LiveCoachingActions from 'actions/scorecards/LiveCoachingActions';
import LiveCoachingSource from 'sources/scorecards/LiveCoachingSource';
import './LiveCoachingModals.styl';
import connectToStores from 'alt-utils/lib/connectToStores';
import SkillStore from 'stores/SkillStore';
import withCoachingPlanWizard from 'components/coaching/wizard/withCoachingPlanWizard';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import LiveCoachingStore from 'stores/scorecards/LiveCoachingStore';
import ScorecardQuestionnaireStore from 'stores/scorecards/ScorecardQuestionnaireStore';
import FileUploadScorecardActions from 'actions/scorecards/FileUploadScorecardActions';
import FileUploadCallCardActions from 'actions/FileUploadCallCardActions';
import {Map} from 'immutable';
import throttle from 'lodash/throttle';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';

const lowSkillCutoff = 3;

@connectToStores
class LiveCoachingModals extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitted: false,
      skillScores: undefined,
      error: false,
    };

    this.submit = throttle(this.submit.bind(this), 5000);
    this.onPlanCTA = this.onPlanCTA.bind(this);
  }

  static getStores() {
    return [SkillStore, LiveCoachingStore, CoachingPlanStore];
  }

  static getPropsFromStores({rep}) {
    const {coachingPlans} = CoachingPlanStore.getState();
    const {usedSkills} = SkillStore.getState();
    const {callRecordingLink, generalComments} = LiveCoachingStore.getState();

    const planSkills = coachingPlans
      .filter(p => rep && p.recipient.id === rep.id)
      .flatMap(p => p.contentDetails)
      .map(detail => detail.skillId)
      .toSet();
    const skillsWithoutPlan = usedSkills.filter(s => {
      // don't suggest skills which the rep already has a plan for
      return !planSkills.has(s.id);
    });

    return {
      callRecordingLink,
      generalComments,
      skills: skillsWithoutPlan,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!prevProps.finalizing && this.props.finalizing && this.props.skipConfirm) {
      // Skip confirmation if scorecards are all scored
      // and model has changed.
      this.submit().catch(console.error);
    }
  }

  submitAnswers() {
    const {scorecards, modelId, taggedUsers = Map(), sessionUser, answers} = this.props;
    if (!answers) return [];

    return answers.keySeq().map(scorecardId => {
      const scorecard = scorecards.find(s => s.id === scorecardId);
      return (
        scorecard &&
        ScorecardQuestionnaireStore.submitScorecardResults(
          modelId,
          sessionUser.id,
          scorecard,
          taggedUsers.get(scorecard.id)
        ).then(() => {
          FileUploadScorecardActions.clearAnswers(modelId, scorecard.id);
          FileUploadCallCardActions.toggleScorecardResults(true);
        })
      );
    });
  }

  async submit() {
    const {review, generalComments, call, callRecordingLink, plan, reviewTypeId, onFinish} =
      this.props;
    try {
      // Answers need to save first so skill calculation is correct
      await Promise.all(this.submitAnswers());

      const [skillScores] = await Promise.all([
        LiveCoachingSource.scoresBySkill(call.id),
        LiveCoachingActions.update(call),
        // this will finish out the plan, add the call, save the score,
        // and make you breakfast
        LiveCoachingSource.finishLivecoachingReview(
          review
            .set('reviewTypeId', reviewTypeId)
            .update('status', status => (plan.livecoaching ? 'COMPLETED' : status)),
          call.id,
          generalComments,
          callRecordingLink
        ),
      ]);
      this.setState({submitted: true, skillScores});
      if (!plan.livecoaching) {
        onFinish();
      }
    } catch (e) {
      console.error(e);
      this.setState({error: true});
    }
  }

  getLowestSkillScore(skillScores, skills) {
    const lowestSkillScore = skillScores
      .filter(s => s.averageScore != null)
      .sort((l, r) => l.averageScore - r.averageScore)
      .first();

    if (!lowestSkillScore || !lowestSkillScore.skillId) return;
    if (lowestSkillScore.averageScore > lowSkillCutoff) return;

    return skills.find(s => s.id === lowestSkillScore.skillId);
  }

  onPlanCTA(badSkill) {
    this.setState({submitted: false});
    this.props.onCreatePlan({
      coach: this.props.coach,
      rep: this.props.rep,
      skillName: badSkill.skill,
      onHideDialog: this.props.onFinish,
    });
  }

  getDialogContent() {
    const {
      rep,
      skills,
      finalizing,
      onCancelSubmit,
      isEditingScorecard,
      hasSubmittedScorecards,
      onFinish,
      plan,
    } = this.props;
    const {submitted, error, skillScores} = this.state;
    if (!finalizing) return;

    let badSkill;
    if (
      plan.livecoaching &&
      submitted &&
      skillScores &&
      !skillScores.isEmpty() &&
      !skills.isEmpty()
    ) {
      badSkill = this.getLowestSkillScore(skillScores, skills);
    }

    if (error) {
      return [
        'AN ERROR OCCURRED',
        'livecoaching-error-modal',
        onFinish,
        <ErrorDialog onFinish={onFinish} />,
      ];
    }

    if (submitted) {
      return [
        'COACHING SESSION SAVED!',
        'livecoaching-post-save-modal',
        onFinish,
        <SavedDialog
          onFinish={onFinish}
          badSkill={badSkill}
          rep={rep}
          onPlanCTA={this.onPlanCTA}
        />,
      ];
    } else {
      return [
        'ARE YOU DONE SCORING?',
        'livecoaching-finalize-modal',
        onCancelSubmit,
        <ConfirmationDialog
          onCancelSubmit={onCancelSubmit}
          isEditingScorecard={isEditingScorecard}
          hasSubmittedScorecards={hasSubmittedScorecards}
          onSubmit={this.submit}
        />,
      ];
    }
  }

  render() {
    // reusing the dialog like this to avoid flickering
    const params = this.getDialogContent();
    if (!params) return null;
    const [title, className, onRequestClose, content] = params;

    return (
      <Dialog open title={title} contentClassName={className} onRequestClose={onRequestClose}>
        {content}
      </Dialog>
    );
  }
}

function ConfirmationDialog({
  onSubmit,
  onCancelSubmit,
  isEditingScorecard,
  hasSubmittedScorecards,
}) {
  return (
    <div>
      <p>
        This will end the coaching session and save all in-progress scorecards. Click yes to
        proceed, or no to keep the session active.
      </p>
      {!hasSubmittedScorecards && !isEditingScorecard && (
        <div className="warning">
          <p>
            Note! You didn't provide any scores, so no scoring information will be retained in the
            system.
          </p>
        </div>
      )}
      <div className="buttons-wrapper">
        <div className={'button submit'} onClick={onSubmit}>
          <div>Yes, I'm done</div>
        </div>
        <div className={'button cancel'} onClick={onCancelSubmit}>
          <div>No, keep session active</div>
        </div>
      </div>
    </div>
  );
}

function SavedDialog({onFinish, badSkill, rep, onPlanCTA}) {
  return (
    <div>
      <p>
        Good job - your feedback has been saved, and you'll be able to view the scoring information
        via the Dashboard skills heatmap.
      </p>
      {badSkill && (
        <CallToAction
          text={
            <div>
              <p>
                Hey! It looks like you scored questions relating to{' '}
                <strong>{badSkill.skill}</strong> particularly low.
              </p>
              <p>
                We suggest creating a coaching plan to work with <strong>{rep.firstName}</strong> to
                methodically improve their skills around <strong>{badSkill.skill}</strong> over a
                series of 4 weekly meetings.
              </p>
              <p>Would you like to create one?</p>
            </div>
          }
        />
      )}
      {badSkill ? (
        <div className="buttons-wrapper">
          <div className={'button submit'} onClick={() => onPlanCTA(badSkill)}>
            <div>Yes - Create a plan</div>
          </div>
          <div className={'button cancel'} onClick={onFinish}>
            <div>No - I'm finished</div>
          </div>
        </div>
      ) : (
        <div className="buttons-wrapper">
          <div className={'button submit'} onClick={onFinish}>
            <div>Done</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorDialog({onFinish}) {
  return (
    <div>
      <p>An error occurred saving the details of the call. Please contact us for help.</p>
      <div className="buttons-wrapper">
        <div className="button submit" onClick={onFinish}>
          <div>Finish</div>
        </div>
      </div>
    </div>
  );
}

export default withCoachingPlanWizard(LiveCoachingModals);
